import { motion } from 'framer-motion'
import { useState } from 'react'
import { Check, RefreshCw } from 'lucide-react'
import type { Tool } from '../../../../types'
import ToolLibraryEditor from '../../ToolLibraryEditor'

interface Props {
  tools: Tool[]
  confirmed: boolean
  onToolsChange: (tools: Tool[]) => void
  onConfirm: () => void
  onResuggest: (prompt?: string) => void
}

export default function AgentToolsBlock({ tools, confirmed, onToolsChange, onConfirm, onResuggest }: Props) {
  const [showPrompt, setShowPrompt] = useState(false)
  const [prompt, setPrompt] = useState('')

  if (confirmed) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        className="flex items-center gap-2 py-0.5"
      >
        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <span className="font-mono text-xs text-slate-500">
          {tools.length} tool{tools.length !== 1 ? 's' : ''} confirmed
        </span>
      </motion.div>
    )
  }

  const handleResuggest = () => {
    onResuggest(prompt.trim() || undefined)
    setPrompt('')
    setShowPrompt(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="my-3 rounded-lg border border-cyan-500/20 bg-white/[0.03] overflow-hidden"
    >
      <div className="px-4 py-2 border-b border-cyan-500/10">
        <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">
          Suggested Tools — review and confirm
        </span>
      </div>

      <div className="px-4 py-3">
        <ToolLibraryEditor
          tools={tools}
          operationType="milling"
          onChange={onToolsChange}
        />
      </div>

      <div className="px-4 py-3 border-t border-cyan-500/10 space-y-2">
        {showPrompt && (
          <form
            onSubmit={e => { e.preventDefault(); handleResuggest() }}
            className="flex gap-2"
          >
            <input
              type="text"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="e.g. Use smaller endmills, add a drill for the holes..."
              autoFocus
              className="flex-1 bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-400 focus:outline-none transition-colors"
            />
            <button
              type="submit"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs border border-cyan-500/30 text-cyan-400 hover:border-cyan-400 hover:bg-cyan-500/10 transition-all duration-150"
            >
              <RefreshCw className="w-3 h-3" />
              Go
            </button>
          </form>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => showPrompt ? handleResuggest() : setShowPrompt(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs border border-slate-600 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400 hover:bg-cyan-500/5 transition-all duration-150"
          >
            <RefreshCw className="w-3 h-3" />
            Re-suggest
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-lg font-mono text-xs font-semibold border border-emerald-500/30 text-emerald-400 hover:border-emerald-400 hover:bg-emerald-500/10 transition-all duration-150"
          >
            Confirm Tools
          </button>
        </div>
      </div>
    </motion.div>
  )
}
